import React, { Suspense, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { useKeyDown } from "../../util/useKeyDown";
import { ModalData, ModalKind, hideModal } from "./modalSlice";
import { ModalPayloadMap } from "./modalTypes";
import { BoardMembersModal } from "./modals/BoardMembersModal";
import { CreateOrEditBoardModal } from "./modals/CreateOrEditBoardModal";
import { EditProfileModal } from "./modals/EditProfileModal";

const ConfigureSessionModal = React.lazy(() =>
  import("./modals/ConfigureSessionModal").then(m => ({ default: m.ConfigureSessionModal }))
);
const CreateOrEditCategoryModal = React.lazy(() =>
  import("./modals/CreateOrEditCategoryModal").then(m => ({ default: m.CreateOrEditCategoryModal }))
);
const CreateOrEditItemModal = React.lazy(() =>
  import("./modals/CreateOrEditItemModal").then(m => ({ default: m.CreateOrEditItemModal }))
);
import { FeaturesModal } from "./modals/FeaturesModal";
import { LoadingSessions } from "./modals/LoadingSessions";
import { ResetPasswordModal } from "./modals/ResetPasswordModal";
import { PresentationTimeModal } from "./modals/PresentationTimeModal";
import { ShareBoardModal } from "./modals/ShareBoardModal";
import { ConfirmDeleteAccountModal } from "./modals/confirm/ConfirmDeleteAccountModal";
import { ConfirmShredBoardModal } from "./modals/confirm/ConfirmShredBoardModal";
import { ConfirmShredSessionModal } from "./modals/confirm/ConfirmShredSessionModal";
import { ConfirmUnsubscribeBoardModal } from "./modals/confirm/ConfirmUnsubscribeBoardModal";

export type ModalProps = { closeModal: () => any }

type ModalRenderer<K extends ModalKind> = (payload: ModalPayloadMap[K], closeModal: () => void) => JSX.Element;

export const MODAL_RENDERERS: { [K in ModalKind]: ModalRenderer<K> } = {
  boardMembers:            (p, close) => <BoardMembersModal boardUid={p.boardUid} ownerUid={p.ownerUid} closeModal={close} />,
  configureSession:        (p, close) => <ConfigureSessionModal sessionData={p.sessionData} closeModal={close} />,
  confirmDeleteAccount:    (_p, close) => <ConfirmDeleteAccountModal closeModal={close} />,
  confirmShredBoard:       (p, close) => <ConfirmShredBoardModal boardData={p.boardData} closeModal={close} />,
  confirmShredSession:     (p, close) => <ConfirmShredSessionModal sessionData={p.sessionData} closeModal={close} />,
  confirmUnsubscribeBoard: (p, close) => <ConfirmUnsubscribeBoardModal boardUid={p.boardUid} userUid={p.userUid} closeModal={close} />,
  createOrEditBoard:       (p, close) => <CreateOrEditBoardModal boardData={p.boardData} closeModal={close} />,
  createOrEditCategory:    (p, close) => <CreateOrEditCategoryModal categoryData={p.categoryData} closeModal={close} />,
  createOrEditItem:        (p, close) => <CreateOrEditItemModal categoryData={p.categoryData} itemData={p.itemData} closeModal={close} />,
  editProfile:             (p, close) => <EditProfileModal name={p.name} closeModal={close} />,
  features:                ()         => <FeaturesModal />,
  loadingSession:          ()         => <LoadingSessions />,
  presentationTime:        (p, close) => <PresentationTimeModal boardUid={p.boardUid} sessionUid={p.sessionUid} firstItemUid={p.firstItemUid} closeModal={close} />,
  resetPassword:           (_p, close) => <ResetPasswordModal closeModal={close} />,
  shareBoard:              (p, close) => <ShareBoardModal boardUid={p.boardUid} closeModal={close} />,
};

function renderModal(data: ModalData, closeModal: () => void): JSX.Element {
  const { kind, ...payload } = data;
  const renderer = MODAL_RENDERERS[kind] as ModalRenderer<typeof kind>;
  return renderer(payload as ModalPayloadMap[typeof kind], closeModal);
}

export type ModalManagerProps = { data: ModalData }

export function ModalManager({ data }: ModalManagerProps) {
  const dispatch = useDispatch<AppDispatch>()

  const handleHideModal = useCallback(
    () => dispatch(hideModal()),
    [dispatch]
  )

  useKeyDown(handleHideModal, ["Escape"])

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={handleHideModal}></div>
      <div className="modal-content">
        <section className="section">
          <div className="container">
            <Suspense fallback={null}>
              {renderModal(data, handleHideModal)}
            </Suspense>
          </div>
        </section>
      </div>
      <div className="modal-close is-large" onClick={handleHideModal}></div>
    </div>
  )
}
